import { VNominationDocumentTypes, type VNominationDocumentType } from "@constants/certificates";
import { convertUaeToUtc, isAfterToday, isBeforeToday } from "@utils/date-time";
import {
  optionalBoolean,
  optionalDate,
  optionalNumberMax,
  optionalStringMax,
  requiredBoolean,
  requiredDate,
  requiredNumberMax,
  requiredStringMax,
  requiredTrue,
} from "@utils/schemas";
import type { TFunction } from "i18next";
import { z } from "zod";
import { NominationDocumentFields } from "../../utils";
import {
  CARGO_ONBOARD_FORM_FIELDS,
  FORM_FIELDS,
  LOADED_VESSEL_CONDITIONS,
  REMARKS_INSTRUCTIONS_MAX_CHARS,
} from "../constants";

const MAIN_LANG_BASE = "vessel-nomination";
const LANG_BASE = "vessel-nomination.add.form.fields";
const PARCEL_LANG_BASE = "vessel-nomination.add.form.parcels.side-drawer";

const normalizeDraftMissingValue = (value: unknown) => {
  if (value == null) return undefined;
  if (typeof value === "string" && value.trim() === "") return undefined;

  return value;
};

const optionalWhenDraft = <T extends z.ZodTypeAny>(
  schema: T,
  isDraft: boolean,
  normalizeValue: (value: unknown) => unknown = normalizeDraftMissingValue,
): T => {
  if (!isDraft) return schema;

  return z.preprocess(normalizeValue, schema.optional()) as unknown as T;
};

export const getCargoOnboardParcelSchema = (t: TFunction, isDraft = false) =>
  z.object({
    [CARGO_ONBOARD_FORM_FIELDS.ID]: z.string().nullish(),
    [CARGO_ONBOARD_FORM_FIELDS.TERMINAL]: optionalWhenDraft(
      z.string().min(
        1,
        t("form-validation.mandatory", {
          label: t(`${PARCEL_LANG_BASE}.terminal`),
        }),
      ),
      isDraft,
    ),
    [CARGO_ONBOARD_FORM_FIELDS.OPERATION_TYPE]: optionalWhenDraft(
      z.string().min(
        1,
        t("form-validation.mandatory", {
          label: t(`${PARCEL_LANG_BASE}.operation-type`),
        }),
      ),
      isDraft,
    ),
    [CARGO_ONBOARD_FORM_FIELDS.CARGO_TYPE]: optionalWhenDraft(
      z.string().min(
        1,
        t("form-validation.mandatory", {
          label: t(`${PARCEL_LANG_BASE}.cargo-type`),
        }),
      ),
      isDraft,
    ),
    [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_CBM]: optionalWhenDraft(
      requiredNumberMax(12, 3, t(`${PARCEL_LANG_BASE}.cargo-quantity-cbm`)),
      isDraft,
    ),
    [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_MT]: optionalWhenDraft(
      requiredNumberMax(12, 3, t(`${PARCEL_LANG_BASE}.cargo-quantity-mt`)),
      isDraft,
    ),
  });

const nominationDocumentTypes = Object.values(
  VNominationDocumentTypes,
) as VNominationDocumentType[];

const REQUIRED_DOCUMENT_TYPES: VNominationDocumentType[] = [
  VNominationDocumentTypes.NOR,
  VNominationDocumentTypes.MASTER_COMPLIANCE,
  VNominationDocumentTypes.STOWAGE_PLAN,
  VNominationDocumentTypes.H2S_LOG,
  VNominationDocumentTypes.TEMPERATURE_LOG,
  VNominationDocumentTypes.LAST_PORT_CLEARANCE,
];

const buildNominationDocumentSchema = (
  t: TFunction,
  documentType: VNominationDocumentType,
  isDraft = false,
) => {
  const isRequiredDocument = REQUIRED_DOCUMENT_TYPES.includes(documentType);

  return z.object({
    [NominationDocumentFields.URL]: isRequiredDocument
      ? optionalWhenDraft(
          z.string().min(1, {
            message: t("form-validation.required", {
              label: t(`${MAIN_LANG_BASE}.add.form.sections.${documentType}`),
            }),
          }),
          isDraft,
        )
      : z.string().optional(),
    [NominationDocumentFields.DOCUMENT_NAME]: z.string(),
    [NominationDocumentFields.TYPE]: z.string().optional(),
    [NominationDocumentFields.OTHER_SPECIFY]: optionalStringMax(
      30,
      t(`${MAIN_LANG_BASE}.add.form.fields.other-specify`),
    ),
    [NominationDocumentFields.ISSUE_DATE]: optionalDate(),
    [NominationDocumentFields.EXPIRY_DATE]: optionalDate(),
    [NominationDocumentFields.NOR_TIME]: optionalDate(),
  });
};

type NominationDocumentSchema = ReturnType<typeof buildNominationDocumentSchema>;
type NominationDocumentShape = Record<VNominationDocumentType, NominationDocumentSchema>;

const buildNominationDocumentShape = (t: TFunction, isDraft = false): NominationDocumentShape => {
  const shape = {} as NominationDocumentShape;

  for (const documentType of nominationDocumentTypes) {
    shape[documentType] = buildNominationDocumentSchema(t, documentType, isDraft);
  }

  return shape;
};

export const getVesselNominationFormSchema = (t: TFunction, isDraft = false) => {
  const requiredNumberMaxSchema = (maxDigits: number, maxDecimals: number, label: string) =>
    optionalWhenDraft(requiredNumberMax(maxDigits, maxDecimals, label), isDraft);

  const requiredStringMaxSchema = (max: number, label: string) =>
    optionalWhenDraft(requiredStringMax(max, label), isDraft);

  const requiredBooleanSchema = (label: string) =>
    optionalWhenDraft(requiredBoolean(label), isDraft);

  const requiredSelectionSchema = (label: string) =>
    optionalWhenDraft(z.string().min(1, t("form-validation.mandatory", { label })), isDraft);

  const requiredTrueSchema = (message: string) =>
    optionalWhenDraft(requiredTrue(message), isDraft, (value) =>
      value === false ? undefined : normalizeDraftMissingValue(value),
    );

  const cargoOnboardParcelSchema = getCargoOnboardParcelSchema(t, isDraft);

  return z
    .object({
      [FORM_FIELDS.VESSEL_ID]: z.string(),
      [FORM_FIELDS.RTA]: requiredDate(),
      [FORM_FIELDS.ETA]: requiredDate(),
      [FORM_FIELDS.CALL_SIGN]: z.string(),
      [FORM_FIELDS.PREVIOUS_VESSEL_NAME]: optionalStringMax(
        50,
        t(`${LANG_BASE}.previous-vessel-name`),
      ),
      [FORM_FIELDS.LOA]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.loa`)),
      [FORM_FIELDS.BALLAST]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.ballast`)),
      [FORM_FIELDS.DRAUGHT_SUMMER]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.draught-summer`)),
      [FORM_FIELDS.DRAUGHT_TROPICAL]: requiredNumberMaxSchema(
        8,
        3,
        t(`${LANG_BASE}.draught-tropical`),
      ),
      [FORM_FIELDS.ARRIVAL_FWD]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.fwd`)),
      [FORM_FIELDS.ARRIVAL_MID]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.mid`)),
      [FORM_FIELDS.ARRIVAL_AFT]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.aft`)),
      [FORM_FIELDS.MAX_TRIM]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.max-trim`)),
      [FORM_FIELDS.DEPARTURE_FWD]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.fwd`)),
      [FORM_FIELDS.DEPARTURE_MID]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.mid`)),
      [FORM_FIELDS.DEPARTURE_AFT]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.aft`)),
      [FORM_FIELDS.ARRIVAL_DRAFT]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.arrival-draft`)),
      [FORM_FIELDS.DEPARTURE_DRAFT]: requiredNumberMaxSchema(
        8,
        3,
        t(`${LANG_BASE}.departure-draft`),
      ),
      [FORM_FIELDS.NET_TONNAGE]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.net-tonnage`)),
      [FORM_FIELDS.GROSS_TONNAGE]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.gross-tonnage`)),
      [FORM_FIELDS.LIGHT]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.light`)),
      [FORM_FIELDS.LOADED]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.loaded`)),
      [FORM_FIELDS.ARRIVAL]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.arrival`)),
      [FORM_FIELDS.DEPARTURE]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.departure`)),
      [FORM_FIELDS.SUMMER_DWT]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.summer-dwt`)),
      [FORM_FIELDS.TROPICAL_DWT]: requiredNumberMaxSchema(12, 3, t(`${LANG_BASE}.tropical-dwt`)),
      [FORM_FIELDS.MANIFOLDS_SIZE]: requiredStringMaxSchema(20, t(`${LANG_BASE}.size`)),
      [FORM_FIELDS.MANIFOLDS_NUMBER]: requiredNumberMaxSchema(10, 0, t(`${LANG_BASE}.number`)),
      [FORM_FIELDS.MANIFOLDS_DISTANCE]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-distance`),
      ),
      [FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-height-grafting`),
      ),
      [FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-height-ballast`),
      ),
      [FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-height-loaded`),
      ),
      [FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-extreme-height`),
      ),
      [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-distance-bow-bcm-stern`),
      ),
      [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-distance-bow-scm-stern`),
      ),
      [FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.manifolds-distance-inboard`),
      ),
      [FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION]: requiredBooleanSchema(
        t(`${LANG_BASE}.terminal-loading-arm-connection`),
      ),
      [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM]: requiredBooleanSchema(
        t(`${LANG_BASE}.exhaust-gas-cleaning-system`),
      ),
      [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE]: z.string(),
      [FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED]: requiredBooleanSchema(
        t(`${LANG_BASE}.confirm-inert-gas-system-fitted`),
      ),
      [FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL]: requiredBooleanSchema(
        t(`${LANG_BASE}.is-inert-gas-system-operational`),
      ),
      [FORM_FIELDS.VESSEL_CONDITION]: requiredSelectionSchema(t(`${LANG_BASE}.vessel-condition`)),
      [FORM_FIELDS.CARGO_TYPE]: optionalStringMax(50, t(`${LANG_BASE}.cargo-type`)),
      [FORM_FIELDS.CARGO_TYPE_OTHER]: optionalStringMax(50, t(`${LANG_BASE}.cargo-type-other`)),
      [FORM_FIELDS.CARGO_QUANTITY_M3]: optionalNumberMax(
        12,
        3,
        t(`${LANG_BASE}.cargo-quantity-m3`),
      ),
      [FORM_FIELDS.CARGO_QUANTITY]: optionalNumberMax(12, 3, t(`${LANG_BASE}.cargo-quantity`)),
      [FORM_FIELDS.PARCELS]: isDraft
        ? z.array(cargoOnboardParcelSchema)
        : z.array(cargoOnboardParcelSchema).min(
            1,
            t("form-validation.mandatory", {
              label: t("vessel-nomination.add.form.sections.type-and-quantity-of-cargo"),
            }),
          ),
      [FORM_FIELDS.FLASH_POINT]: optionalNumberMax(6, 3, t(`${LANG_BASE}.flash-point`)),
      [FORM_FIELDS.SULPHUR]: optionalNumberMax(5, 3, t(`${LANG_BASE}.sulphur`)),
      [FORM_FIELDS.MERCAPTAN]: optionalNumberMax(8, 3, t(`${LANG_BASE}.mercaptan`)),
      [FORM_FIELDS.STOWED_CARGO_TEMPERATURE]: optionalNumberMax(
        6,
        3,
        t(`${LANG_BASE}.stowed-cargo-temperature`),
      ),
      [FORM_FIELDS.HYDROGEN_SULPHIDE]: optionalNumberMax(8, 3, t(`${LANG_BASE}.hydrogen-sulphide`)),
      [FORM_FIELDS.RVP]: optionalNumberMax(6, 3, t(`${LANG_BASE}.rvp`)),
      [FORM_FIELDS.COLOUR_ASTM]: optionalStringMax(20, t(`${LANG_BASE}.colour-astm`)),
      [FORM_FIELDS.BENZENE_CONTENT]: optionalNumberMax(5, 3, t(`${LANG_BASE}.benzene-content`)),
      [FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE]: optionalBoolean(),
      [FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL]: optionalNumberMax(
        8,
        3,
        t(`${LANG_BASE}.avg-pressure-cargo-tanks-arrival`),
      ),
      [FORM_FIELDS.MAX_PRESSURE_MANIFOLDS]: requiredNumberMaxSchema(
        6,
        3,
        t(`${LANG_BASE}.max-pressure-manifolds`),
      ),
      [FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED]: optionalBoolean(),
      [FORM_FIELDS.MOORING_FWD]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.fwd`)),
      [FORM_FIELDS.MOORING_MID]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.mid`)),
      [FORM_FIELDS.MOORING_AFT]: requiredNumberMaxSchema(8, 3, t(`${LANG_BASE}.aft`)),
      [FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES]: requiredBooleanSchema(
        t(`${LANG_BASE}.mooring-details-and-deficiencies`),
      ),
      [FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES]: optionalStringMax(
        500,
        t(`${LANG_BASE}.mooring-details-and-deficiencies`),
      ),
      [FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: requiredBooleanSchema(
        t(`${LANG_BASE}.any-defects-hull-machinery-equipment`),
      ),
      [FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: optionalStringMax(
        500,
        t(`${LANG_BASE}.any-defects-hull-machinery-equipment`),
      ),
      [FORM_FIELDS.HAS_AREA_WITH_PROTRUSION]: requiredBooleanSchema(
        t(`${LANG_BASE}.area-with-protrusion`),
      ),
      [FORM_FIELDS.AREA_WITH_PROTRUSION]: optionalStringMax(
        100,
        t(`${LANG_BASE}.area-with-protrusion`),
      ),
      [FORM_FIELDS.LAST_SIRE_INSPECTION]: requiredDate(),
      [FORM_FIELDS.ISSUING_AUTHORITY]: requiredStringMaxSchema(
        50,
        t(`${LANG_BASE}.issuing-authority`),
      ),
      [FORM_FIELDS.LAST_PSC_INSPECTION]: requiredDate(),
      [FORM_FIELDS.PLACE]: requiredStringMaxSchema(50, t(`${LANG_BASE}.place`)),
      [FORM_FIELDS.LAST_PORT]: requiredSelectionSchema(t(`${LANG_BASE}.last-port-of-call`)),
      [FORM_FIELDS.NEXT_PORT]: requiredSelectionSchema(t(`${LANG_BASE}.next-port-of-call`)),
      [FORM_FIELDS.SECURITY_LEVEL]: requiredSelectionSchema(t(`${LANG_BASE}.security-level`)),
      [FORM_FIELDS.PAQ_DOCUMENT]: z.object({
        documentName: z.string(),
        url: optionalWhenDraft(
          z.string().min(
            1,
            t("form-validation.mandatory", {
              label: t("vessel-nomination.add.form.sections.paq-document"),
            }),
          ),
          isDraft,
        ),
      }),
      [FORM_FIELDS.CONFIRMATION]: requiredTrueSchema(t("form-validation.signature-agreement")),
      [FORM_FIELDS.REMARKS]: optionalStringMax(
        REMARKS_INSTRUCTIONS_MAX_CHARS,
        t(`${LANG_BASE}.remarks`),
      ),
      [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS]: z
        .array(
          z.object({
            documentUrl: z.string(),
            documentName: z.string(),
            documentType: optionalWhenDraft(
              z.string().min(
                1,
                t("form-validation.required", {
                  label: t(`${MAIN_LANG_BASE}.add.form.fields.document-type`),
                }),
              ),
              isDraft,
            ),
            otherSpecify: optionalStringMax(
              30,
              t(`${MAIN_LANG_BASE}.add.form.fields.other-specify`),
            ),
            issueDate: optionalDate(),
            expiryDate: optionalDate(),
          }),
        )
        .max(25)
        .optional(),
      [FORM_FIELDS.PENDING_UPLOADS]: z.number().default(0),
      [FORM_FIELDS.DOCUMENTS]: z.object(buildNominationDocumentShape(t, isDraft)),
    })
    .superRefine((data, ctx) => {
      const eta = data[FORM_FIELDS.ETA];

      if (!(eta instanceof Date)) {
        if (!isDraft) {
          ctx.addIssue({
            path: [FORM_FIELDS.ETA],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.eta`),
            }),
          });
        }
      } else {
        const selectedUaeDateTime = new Date(convertUaeToUtc(eta));
        const now = new Date();

        if (selectedUaeDateTime < now) {
          ctx.addIssue({
            path: [FORM_FIELDS.ETA],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${LANG_BASE}.eta`),
            }),
          });
        }
      }

      const rta = data[FORM_FIELDS.RTA];

      if (!(rta instanceof Date)) {
        if (!isDraft) {
          ctx.addIssue({
            path: [FORM_FIELDS.RTA],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.rta`),
            }),
          });
        }
      } else {
        const selectedUaeDateTime = new Date(convertUaeToUtc(rta));
        const now = new Date();

        if (selectedUaeDateTime < now) {
          ctx.addIssue({
            path: [FORM_FIELDS.RTA],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${LANG_BASE}.rta`),
            }),
          });
        }
      }

      const arrivalMid = data[FORM_FIELDS.ARRIVAL_MID];
      const arrivalFwd = data[FORM_FIELDS.ARRIVAL_FWD];
      const arrivalAft = data[FORM_FIELDS.ARRIVAL_AFT];
      const departureMid = data[FORM_FIELDS.DEPARTURE_MID];
      const departureFwd = data[FORM_FIELDS.DEPARTURE_FWD];
      const departureAft = data[FORM_FIELDS.DEPARTURE_AFT];
      const mooringMid = data[FORM_FIELDS.MOORING_MID];
      const mooringFwd = data[FORM_FIELDS.MOORING_FWD];
      const mooringAft = data[FORM_FIELDS.MOORING_AFT];

      if (arrivalMid && arrivalFwd && arrivalMid < arrivalFwd) {
        ctx.addIssue({
          path: [FORM_FIELDS.ARRIVAL_MID],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.mid`),
            min: t(`${LANG_BASE}.fwd`),
          }),
        });
      }

      if (arrivalAft && arrivalMid && arrivalAft < arrivalMid) {
        ctx.addIssue({
          path: [FORM_FIELDS.ARRIVAL_AFT],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.aft`),
            min: t(`${LANG_BASE}.mid`),
          }),
        });
      }

      if (departureMid && departureFwd && departureMid < departureFwd) {
        ctx.addIssue({
          path: [FORM_FIELDS.DEPARTURE_MID],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.mid`),
            min: t(`${LANG_BASE}.fwd`),
          }),
        });
      }

      if (departureAft && departureMid && departureAft < departureMid) {
        ctx.addIssue({
          path: [FORM_FIELDS.DEPARTURE_AFT],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.aft`),
            min: t(`${LANG_BASE}.mid`),
          }),
        });
      }

      const loa = data[FORM_FIELDS.LOA];
      const maxTrim = data[FORM_FIELDS.MAX_TRIM];

      if (loa && maxTrim && maxTrim > loa * 0.015) {
        ctx.addIssue({
          path: [FORM_FIELDS.MAX_TRIM],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.exceeds-allowed-trim-limits"),
        });
      }

      const light = data[FORM_FIELDS.LIGHT];
      const loaded = data[FORM_FIELDS.LOADED];
      if (light && loaded && light >= loaded) {
        ctx.addIssue({
          path: [FORM_FIELDS.LOADED],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater", {
            label: t(`${LANG_BASE}.loaded`),
            min: t(`${LANG_BASE}.light`),
          }),
        });
      }

      if (
        !isDraft &&
        data[FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM] &&
        !data[FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE]
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t(`${LANG_BASE}.exhaust-gas-cleaning-system-type`),
          }),
        });
      }

      if (
        !isDraft &&
        data[FORM_FIELDS.VESSEL_CONDITION] &&
        LOADED_VESSEL_CONDITIONS.includes(data[FORM_FIELDS.VESSEL_CONDITION])
      ) {
        // CARGO ONBOARD
        const cargoType = data[FORM_FIELDS.CARGO_TYPE];
        if (!cargoType) {
          ctx.addIssue({
            path: [FORM_FIELDS.CARGO_TYPE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.cargo-type`),
            }),
          });
        }
        if (!data[FORM_FIELDS.CARGO_QUANTITY_M3]) {
          ctx.addIssue({
            path: [FORM_FIELDS.CARGO_QUANTITY_M3],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.cargo-quantity-m3`),
            }),
          });
        }
        if (!data[FORM_FIELDS.CARGO_QUANTITY]) {
          ctx.addIssue({
            path: [FORM_FIELDS.CARGO_QUANTITY],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.cargo-quantity`),
            }),
          });
        }
        if (cargoType === "OT" && !data[FORM_FIELDS.CARGO_TYPE_OTHER]) {
          ctx.addIssue({
            path: [FORM_FIELDS.CARGO_TYPE_OTHER],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.cargo-type-other`),
            }),
          });
        }
        // TOXIC COMPONENT
        if (!data[FORM_FIELDS.FLASH_POINT]) {
          ctx.addIssue({
            path: [FORM_FIELDS.FLASH_POINT],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.flash-point`),
            }),
          });
        }
        if (!data[FORM_FIELDS.SULPHUR]) {
          ctx.addIssue({
            path: [FORM_FIELDS.SULPHUR],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.sulphur`),
            }),
          });
        }
        if (!data[FORM_FIELDS.MERCAPTAN]) {
          ctx.addIssue({
            path: [FORM_FIELDS.MERCAPTAN],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.mercaptan`),
            }),
          });
        }
        if (!data[FORM_FIELDS.STOWED_CARGO_TEMPERATURE]) {
          ctx.addIssue({
            path: [FORM_FIELDS.STOWED_CARGO_TEMPERATURE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.stowed-cargo-temperature`),
            }),
          });
        }
        if (!data[FORM_FIELDS.HYDROGEN_SULPHIDE]) {
          ctx.addIssue({
            path: [FORM_FIELDS.HYDROGEN_SULPHIDE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.hydrogen-sulphide`),
            }),
          });
        }
        if (!data[FORM_FIELDS.RVP]) {
          ctx.addIssue({
            path: [FORM_FIELDS.RVP],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.rvp`),
            }),
          });
        }
        if (!data[FORM_FIELDS.COLOUR_ASTM]) {
          ctx.addIssue({
            path: [FORM_FIELDS.COLOUR_ASTM],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.colour-astm`),
            }),
          });
        }
        if (!data[FORM_FIELDS.BENZENE_CONTENT]) {
          ctx.addIssue({
            path: [FORM_FIELDS.BENZENE_CONTENT],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.benzene-content`),
            }),
          });
        }
        if (data[FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE] === "") {
          ctx.addIssue({
            path: [FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.are-high-h2s-tanks-adequate`),
            }),
          });
        }
        if (!data[FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL]) {
          ctx.addIssue({
            path: [FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.avg-pressure-cargo-tanks-arrival`),
            }),
          });
        }
        if (data[FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED] === "") {
          ctx.addIssue({
            path: [FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.is-crude-oil-washing-operation-planned`),
            }),
          });
        }
      }

      if (mooringMid && mooringFwd && mooringMid < mooringFwd) {
        ctx.addIssue({
          path: [FORM_FIELDS.MOORING_MID],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.mid`),
            min: t(`${LANG_BASE}.fwd`),
          }),
        });
      }

      if (mooringAft && mooringMid && mooringAft < mooringMid) {
        ctx.addIssue({
          path: [FORM_FIELDS.MOORING_AFT],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-greater-or-equal", {
            label: t(`${LANG_BASE}.aft`),
            min: t(`${LANG_BASE}.mid`),
          }),
        });
      }

      if (
        !isDraft &&
        data[FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES] &&
        !data[FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES]
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t(`${LANG_BASE}.mooring-details-and-deficiencies`),
          }),
        });
      }

      if (
        !isDraft &&
        data[FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT] &&
        !data[FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t(`${LANG_BASE}.any-defects-hull-machinery-equipment`),
          }),
        });
      }

      if (
        !isDraft &&
        data[FORM_FIELDS.HAS_AREA_WITH_PROTRUSION] &&
        !data[FORM_FIELDS.AREA_WITH_PROTRUSION]
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.AREA_WITH_PROTRUSION],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t(`${LANG_BASE}.area-with-protrusion`),
          }),
        });
      }

      const lastSireInspection = data[FORM_FIELDS.LAST_SIRE_INSPECTION];
      const lastPscInspection = data[FORM_FIELDS.LAST_PSC_INSPECTION];

      if (!(lastSireInspection instanceof Date)) {
        if (!isDraft) {
          ctx.addIssue({
            path: [FORM_FIELDS.LAST_SIRE_INSPECTION],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.date`),
            }),
          });
        }
      } else {
        const selectedSireInspectionDate = new Date(convertUaeToUtc(lastSireInspection));
        const now = new Date();

        if (selectedSireInspectionDate > now) {
          ctx.addIssue({
            path: [FORM_FIELDS.LAST_SIRE_INSPECTION],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-future", {
              label: t(`${LANG_BASE}.date`),
            }),
          });
        }
      }

      if (!(lastPscInspection instanceof Date)) {
        if (!isDraft) {
          ctx.addIssue({
            path: [FORM_FIELDS.LAST_PSC_INSPECTION],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t(`${LANG_BASE}.date`),
            }),
          });
        }
      } else {
        const selectedPscInspectionDate = new Date(convertUaeToUtc(lastPscInspection));
        const now = new Date();

        if (selectedPscInspectionDate > now) {
          ctx.addIssue({
            path: [FORM_FIELDS.LAST_PSC_INSPECTION],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-future", {
              label: t(`${LANG_BASE}.date`),
            }),
          });
        }
      }

      const documents = data[FORM_FIELDS.DOCUMENTS];

      for (const documentType of nominationDocumentTypes) {
        const document = documents[documentType];
        const selectedDocumentType = document[NominationDocumentFields.TYPE];
        const otherSpecify = document[NominationDocumentFields.OTHER_SPECIFY];
        const issueDate = document[NominationDocumentFields.ISSUE_DATE];
        const expiryDate = document[NominationDocumentFields.EXPIRY_DATE];

        if (!isDraft && selectedDocumentType === "OTHERS_SPECIFY" && !otherSpecify) {
          ctx.addIssue({
            path: [FORM_FIELDS.DOCUMENTS, documentType, NominationDocumentFields.OTHER_SPECIFY],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.other-specify`),
            }),
          });
        }

        if (issueDate instanceof Date && isAfterToday(issueDate)) {
          ctx.addIssue({
            path: [FORM_FIELDS.DOCUMENTS, documentType, NominationDocumentFields.ISSUE_DATE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-future", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.issue-date`),
            }),
          });
        }

        if (expiryDate instanceof Date && isBeforeToday(expiryDate)) {
          ctx.addIssue({
            path: [FORM_FIELDS.DOCUMENTS, documentType, NominationDocumentFields.EXPIRY_DATE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.expiration-date`),
            }),
          });
        }
      }
      const loadingPlanUrl =
        documents[VNominationDocumentTypes.LOADING_PLAN][NominationDocumentFields.URL];

      const dischargePlanUrl =
        documents[VNominationDocumentTypes.DISCHARGE_PLAN][NominationDocumentFields.URL];

      if (!isDraft && !loadingPlanUrl && !dischargePlanUrl) {
        ctx.addIssue({
          path: [
            FORM_FIELDS.DOCUMENTS,
            VNominationDocumentTypes.LOADING_PLAN,
            NominationDocumentFields.URL,
          ],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.loading-or-discharge-plan-required"),
        });

        ctx.addIssue({
          path: [
            FORM_FIELDS.DOCUMENTS,
            VNominationDocumentTypes.DISCHARGE_PLAN,
            NominationDocumentFields.URL,
          ],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.loading-or-discharge-plan-required"),
        });
      }

      const requireIssueDate = (documentType: VNominationDocumentType, labelKey: string) => {
        const document = documents[documentType];
        const documentUrl = document[NominationDocumentFields.URL];
        const issueDate = document[NominationDocumentFields.ISSUE_DATE];

        if (!isDraft && documentUrl && !issueDate) {
          ctx.addIssue({
            path: [FORM_FIELDS.DOCUMENTS, documentType, NominationDocumentFields.ISSUE_DATE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t(labelKey),
            }),
          });
        }
      };
      requireIssueDate(
        VNominationDocumentTypes.MASTER_COMPLIANCE,
        `${MAIN_LANG_BASE}.add.form.fields.issue-date`,
      );

      requireIssueDate(
        VNominationDocumentTypes.LAST_PORT_CLEARANCE,
        `${MAIN_LANG_BASE}.add.form.fields.issue-date`,
      );

      requireIssueDate(
        VNominationDocumentTypes.NOC_HARBOUR_MASTER,
        `${MAIN_LANG_BASE}.add.form.fields.issue-date`,
      );
      const additionalNominationDocuments = data[FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS] ?? [];

      additionalNominationDocuments.forEach((document, index) => {
        if (!isDraft && document.documentType === "OTHERS_SPECIFY" && !document.otherSpecify) {
          ctx.addIssue({
            path: [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS, index, "otherSpecify"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.other-specify`),
            }),
          });
        }

        if (document.issueDate instanceof Date && isAfterToday(document.issueDate)) {
          ctx.addIssue({
            path: [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS, index, "issueDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-future", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.issue-date`),
            }),
          });
        }

        if (document.expiryDate instanceof Date && isBeforeToday(document.expiryDate)) {
          ctx.addIssue({
            path: [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS, index, "expiryDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${MAIN_LANG_BASE}.add.form.fields.expiration-date`),
            }),
          });
        }
      });
    });
};

type VesselNominationFormSchema = ReturnType<typeof getVesselNominationFormSchema>;

export type CargoOnboardParcelFormData = z.input<ReturnType<typeof getCargoOnboardParcelSchema>>;
export type CargoOnboardParcelValidatedFormData = z.output<
  ReturnType<typeof getCargoOnboardParcelSchema>
>;
export type VesselNominationFormData = z.infer<VesselNominationFormSchema>;
export type VesselNominationValidatedFormData = z.output<VesselNominationFormSchema>;
